import React, { useState, useEffect, useCallback } from 'react';
import { chemistryApi } from '../../api/chemistry';

interface Target {
  id: number;
  biologic_id: number;
  interaction_type: string;
  affinity_value: number | null;
  affinity_unit: string | null;
  binding_site?: string | null;
  species?: string | null;
  relationship_type?: string | null;
  target_name: string | null;
  uniprot_id: string | null;
}

interface Biologic {
  id: number;
  name: string | null;
  uniprot_id: string | null;
  sequence_type: string | null;
}

interface LinkTargetModalProps {
  isOpen: boolean;
  onClose: () => void;
  moleculeId: number;
  moleculeSmiles?: string;
  onLinksUpdated?: () => void;
}

// Complete pharmacology taxonomy — Adroit AI's classification
const INTERACTION_GROUPS = [
  {
    group: 'Agonism Spectrum',
    types: [
      { value: 'Full Agonist', label: 'Full Agonist', description: 'Activates receptor to maximal response', color: 'bg-green-100 text-green-800' },
      { value: 'Partial Agonist', label: 'Partial Agonist', description: 'Activates receptor but with submaximal efficacy', color: 'bg-green-100 text-green-700' },
      { value: 'Inverse Agonist', label: 'Inverse Agonist', description: 'Reduces constitutive (baseline) receptor activity', color: 'bg-green-100 text-green-600' },
      { value: 'Biased Agonist', label: 'Biased Agonist', description: 'Activates specific signalling pathways over others (functional selectivity)', color: 'bg-green-100 text-green-500' },
      { value: 'Superagonist', label: 'Superagonist', description: 'Produces greater response than the natural ligand', color: 'bg-emerald-100 text-emerald-800' },
    ],
  },
  {
    group: 'Antagonism Spectrum',
    types: [
      { value: 'Competitive Antagonist', label: 'Competitive Antagonist', description: 'Competes at the orthosteric site (surmountable)', color: 'bg-red-100 text-red-800' },
      { value: 'Non-competitive Antagonist', label: 'Non-competitive Antagonist', description: 'Reduces response regardless of agonist concentration', color: 'bg-red-100 text-red-700' },
      { value: 'Uncompetitive Antagonist', label: 'Uncompetitive Antagonist', description: 'Binds only to the ligand-bound receptor', color: 'bg-red-100 text-red-600' },
      { value: 'Mixed Antagonist', label: 'Mixed Antagonist', description: 'Combination of competitive and non-competitive behaviour', color: 'bg-red-100 text-red-500' },
      { value: 'Neutral Antagonist', label: 'Neutral Antagonist', description: 'Occupies receptor without changing baseline activity', color: 'bg-rose-100 text-rose-700' },
    ],
  },
  {
    group: 'Allosteric Modulation',
    types: [
      { value: 'PAM', label: 'PAM (Positive Allosteric Modulator)', description: 'Enhances agonist binding or efficacy', color: 'bg-purple-100 text-purple-800' },
      { value: 'NAM', label: 'NAM (Negative Allosteric Modulator)', description: 'Reduces agonist binding or efficacy', color: 'bg-purple-100 text-purple-700' },
      { value: 'SAM', label: 'SAM (Silent Allosteric Modulator)', description: 'Binds allosteric site without functional effect (blocks PAMs/NAMs)', color: 'bg-purple-100 text-purple-600' },
    ],
  },
  {
    group: 'Enzyme Interactions',
    types: [
      { value: 'Competitive Inhibitor', label: 'Competitive Inhibitor', description: 'Competes with substrate at the active site', color: 'bg-orange-100 text-orange-800' },
      { value: 'Non-competitive Inhibitor', label: 'Non-competitive Inhibitor', description: 'Binds enzyme at a site other than the active site', color: 'bg-orange-100 text-orange-700' },
      { value: 'Uncompetitive Inhibitor', label: 'Uncompetitive Inhibitor', description: 'Binds only the enzyme-substrate complex', color: 'bg-orange-100 text-orange-600' },
      { value: 'Suicide Inhibitor', label: 'Suicide/Mechanism-based Inhibitor', description: 'Irreversibly inactivates enzyme during catalysis', color: 'bg-amber-100 text-amber-800' },
      { value: 'Transition State Analogue', label: 'Transition State Analogue', description: 'Mimics the transition state of the enzymatic reaction', color: 'bg-amber-100 text-amber-700' },
    ],
  },
  {
    group: 'Other Mechanisms',
    types: [
      { value: 'Substrate', label: 'Substrate', description: 'Acts as a substrate for the enzyme', color: 'bg-gray-100 text-gray-800' },
      { value: 'Pseudo-substrate', label: 'Pseudo-substrate', description: 'Mimics substrate but produces altered outcome', color: 'bg-gray-100 text-gray-700' },
      { value: 'Cofactor Mimic', label: 'Cofactor Mimic', description: 'Mimics or competes with enzyme cofactor', color: 'bg-gray-100 text-gray-600' },
      { value: 'PPI Inhibitor', label: 'PPI Inhibitor', description: 'Inhibits protein-protein interactions', color: 'bg-blue-100 text-blue-800' },
      { value: 'PPI Stabiliser', label: 'PPI Stabiliser', description: 'Stabilises protein-protein interactions', color: 'bg-blue-100 text-blue-700' },
      { value: 'Degrader (PROTAC)', label: 'Degrader (PROTAC)', description: 'Recruits E3 ligase to degrade target protein', color: 'bg-indigo-100 text-indigo-800' },
      { value: 'Unknown', label: 'Unknown', description: 'Interaction mechanism not yet determined', color: 'bg-gray-100 text-gray-500' },
    ],
  },
];

// Flatten for lookups
const ALL_INTERACTION_TYPES = INTERACTION_GROUPS.flatMap(g => g.types);

const BINDING_MODES = [
  { value: 'Orthosteric', label: 'Orthosteric', description: 'Binds the primary (endogenous ligand) site' },
  { value: 'Allosteric', label: 'Allosteric', description: 'Binds a distinct site that modulates the orthosteric site' },
  { value: 'Bitopic', label: 'Bitopic', description: 'Spans orthosteric + allosteric sites simultaneously' },
  { value: 'Covalent', label: 'Covalent', description: 'Forms a covalent bond (often irreversible, sometimes reversible)' },
  { value: 'Non-covalent', label: 'Non-covalent', description: 'Reversible binding via intermolecular forces (most drugs)' },
  { value: 'Conformational Selection', label: 'Conformational Selection', description: 'Binds a pre-existing receptor conformation' },
  { value: 'Transient', label: 'Transient/Weak Binding', description: 'Brief, low-affinity binding interaction' },
  { value: 'Non-specific', label: 'Non-specific (NSP)', description: 'Binds without defined site selectivity' },
  { value: 'Unknown', label: 'Unknown', description: 'Binding mode not determined' },
];

const SPECIES_OPTIONS = [
  { value: 'Homo sapiens', label: 'Human (Homo sapiens)' },
  { value: 'Mus musculus', label: 'Mouse (Mus musculus)' },
  { value: 'Rattus norvegicus', label: 'Rat (Rattus norvegicus)' },
  { value: 'Canis lupus familiaris', label: 'Dog (Canis lupus familiaris)' },
  { value: 'Oryctolagus cuniculus', label: 'Rabbit (Oryctolagus cuniculus)' },
  { value: 'Cavia porcellus', label: 'Guinea Pig (Cavia porcellus)' },
  { value: 'Macaca fascicularis', label: 'Cynomolgus Monkey (Macaca fascicularis)' },
  { value: 'Macaca mulatta', label: 'Rhesus Monkey (Macaca mulatta)' },
  { value: 'Sus scrofa', label: 'Pig (Sus scrofa)' },
  { value: 'Danio rerio', label: 'Zebrafish (Danio rerio)' },
];

const RELATIONSHIP_TYPES = [
  { value: 'Primary Target', label: 'Primary Target', description: 'The main biological target for this project' },
  { value: 'Homologue', label: 'Homologue', description: 'A structurally related protein in the same species' },
  { value: 'Species Orthologue', label: 'Species Orthologue', description: 'The equivalent protein in a different species' },
  { value: 'Anti-target', label: 'Anti-target', description: 'A target to AVOID activity against (selectivity concern)' },
  { value: 'Off-target', label: 'Off-target', description: 'Known unintended target' },
];

const AFFINITY_UNITS = ['pM', 'nM', '\u00B5M', 'mM'];

// Info tooltip component
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex items-center ml-1">
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-bold inline-flex items-center justify-center cursor-help hover:bg-gray-300"
      >
        i
      </span>
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs bg-gray-800 text-white rounded shadow-lg whitespace-nowrap z-50 max-w-[250px] text-center" style={{ whiteSpace: 'normal' }}>
          {text}
        </span>
      )}
    </span>
  );
};

export const LinkTargetModal: React.FC<LinkTargetModalProps> = ({
  isOpen,
  onClose,
  moleculeId,
  moleculeSmiles,
  onLinksUpdated,
}) => {
  const [biologics, setBiologics] = useState<Biologic[]>([]);
  const [linkedTargets, setLinkedTargets] = useState<Target[]>([]);
  const [selectedBiologicId, setSelectedBiologicId] = useState<number | null>(null);
  const [interactionType, setInteractionType] = useState('Unknown');
  const [bindingSite, setBindingSite] = useState('Unknown');
  const [species, setSpecies] = useState('Homo sapiens');
  const [relationshipType, setRelationshipType] = useState('Primary Target');
  const [affinityValue, setAffinityValue] = useState<string>('');
  const [affinityUnit, setAffinityUnit] = useState('nM');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    if (!isOpen) return;

    setLoading(true);
    try {
      const targetsResponse = await chemistryApi.getMoleculeTargets(moleculeId);
      setLinkedTargets(targetsResponse.targets);

      const biologicsResponse = await chemistryApi.getBiologics(undefined, 500, 0);
      setBiologics(biologicsResponse.biologics);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isOpen, moleculeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLink = async () => {
    if (!selectedBiologicId) return;

    setSaving(true);
    try {
      const affVal = affinityValue ? parseFloat(affinityValue) : undefined;
      await chemistryApi.linkMoleculeToTarget(
        moleculeId,
        selectedBiologicId,
        interactionType,
        affVal,
        affVal ? affinityUnit : undefined,
        bindingSite,
        species,
        relationshipType
      );
      setSelectedBiologicId(null);
      setAffinityValue('');
      await loadData();
      onLinksUpdated?.();
    } catch (error) {
      console.error('Error linking target:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (biologicId: number) => {
    try {
      await chemistryApi.unlinkMoleculeFromTarget(moleculeId, biologicId);
      await loadData();
      onLinksUpdated?.();
    } catch (error) {
      console.error('Error unlinking target:', error);
    }
  };

  const getInteractionColor = (type: string): string => {
    const found = ALL_INTERACTION_TYPES.find(t => t.value === type);
    return found?.color || 'bg-gray-100 text-gray-800';
  };

  // Filter biologics by search term and exclude already linked
  const linkedBiologicIds = new Set(linkedTargets.map(t => t.biologic_id));
  const availableBiologics = biologics.filter(b => {
    if (linkedBiologicIds.has(b.id)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      b.name?.toLowerCase().includes(term) ||
      b.uniprot_id?.toLowerCase().includes(term)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-[900px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold">Link Molecule to Targets</h2>
            {moleculeSmiles && (
              <p className="text-sm text-gray-500 font-mono truncate max-w-md">
                {moleculeSmiles.substring(0, 50)}{moleculeSmiles.length > 50 ? '...' : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Help text */}
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700">
          Specify how your compounds interact with this target. The interaction type,
          binding site, and species are important for accurate pharmacological profiling.
          For example: a beta-agonist (e.g. salbutamol) for asthma, or a beta-antagonist
          (e.g. propranolol) for hypertension.
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Select target */}
          <div className="w-1/2 border-r flex flex-col">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-medium text-sm mb-2">Available Targets</h3>
              <input
                type="text"
                placeholder="Search by name or UniProt ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2" style={{ minHeight: 120, maxHeight: 200 }}>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
                </div>
              ) : availableBiologics.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No targets available
                </div>
              ) : (
                availableBiologics.map((biologic) => (
                  <div
                    key={biologic.id}
                    onClick={() => setSelectedBiologicId(biologic.id)}
                    className={`p-2 mb-1 rounded cursor-pointer border ${
                      selectedBiologicId === biologic.id
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{biologic.name || `Biologic #${biologic.id}`}</span>
                      {biologic.sequence_type && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                          {biologic.sequence_type}
                        </span>
                      )}
                    </div>
                    {biologic.uniprot_id && (
                      <p className="text-xs text-gray-500 mt-0.5">{biologic.uniprot_id}</p>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Link controls */}
            <div className="p-3 border-t bg-gray-50 space-y-2">
              {/* Interaction Type - grouped */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 w-20 flex items-center">
                  Interaction
                  <InfoTooltip text="How your compound interacts with this target at the molecular level" />
                </label>
                <select
                  value={interactionType}
                  onChange={(e) => setInteractionType(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                >
                  {INTERACTION_GROUPS.map((group) => (
                    <optgroup key={group.group} label={group.group}>
                      {group.types.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Binding Mode */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 w-20 flex items-center">
                  Binding Mode
                  <InfoTooltip text="How does the compound physically engage with the target?" />
                </label>
                <select
                  value={bindingSite}
                  onChange={(e) => setBindingSite(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                >
                  {BINDING_MODES.map((mode) => (
                    <option key={mode.value} value={mode.value} title={mode.description}>{mode.label}</option>
                  ))}
                </select>
              </div>

              {/* Species */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 w-20 flex items-center">
                  Species
                  <InfoTooltip text="Species of the target protein. Drugs can behave very differently across species." />
                </label>
                <select
                  value={species}
                  onChange={(e) => setSpecies(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                >
                  {SPECIES_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* Relationship Type */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 w-20 flex items-center">
                  Relationship
                  <InfoTooltip text="What role does this target play in your project?" />
                </label>
                <select
                  value={relationshipType}
                  onChange={(e) => setRelationshipType(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                >
                  {RELATIONSHIP_TYPES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {/* Affinity */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 w-20 flex items-center">
                  Affinity
                  <InfoTooltip text="Measured binding affinity (IC50, Ki, Kd, EC50)" />
                </label>
                <input
                  type="number"
                  placeholder="Value"
                  value={affinityValue}
                  onChange={(e) => setAffinityValue(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                  step="0.01"
                  min="0"
                />
                <select
                  value={affinityUnit}
                  onChange={(e) => setAffinityUnit(e.target.value)}
                  className="w-20 px-2 py-1 text-sm border rounded"
                >
                  {AFFINITY_UNITS.map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleLink}
                disabled={!selectedBiologicId || saving}
                className="w-full px-3 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Linking...' : 'Link Target'}
              </button>
            </div>
          </div>

          {/* Right: Linked targets */}
          <div className="w-1/2 flex flex-col">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-medium text-sm">Linked Targets ({linkedTargets.length})</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {linkedTargets.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No linked targets
                </div>
              ) : (
                linkedTargets.map((target) => (
                  <div
                    key={target.id}
                    className="p-2 mb-1 rounded border border-gray-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">
                            {target.target_name || `Target #${target.biologic_id}`}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${getInteractionColor(target.interaction_type)}`}>
                            {target.interaction_type}
                          </span>
                          {target.relationship_type && target.relationship_type !== 'Primary Target' && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">
                              {target.relationship_type}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {target.uniprot_id && (
                            <span className="text-xs text-gray-500">{target.uniprot_id}</span>
                          )}
                          {target.species && target.species !== 'Homo sapiens' && (
                            <span className="text-xs text-gray-400 italic">{target.species}</span>
                          )}
                          {target.binding_site && target.binding_site !== 'Unknown' && (
                            <span className="text-xs text-gray-400">{target.binding_site}</span>
                          )}
                          {target.affinity_value && (
                            <span className="text-xs text-gray-600">
                              {target.affinity_value} {target.affinity_unit}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnlink(target.biologic_id)}
                        className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded flex-shrink-0"
                        title="Remove link"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkTargetModal;
